"use client";

/**
 * 设备绑定申请弹窗 — 未授权设备登录被拦截时弹出
 * 提交加绑申请（extra=永久加绑 / temp=临时授权，默认 2 天）
 * 支持两种提交方式：
 * - 已登录（有 token）：自动带用户身份，无需填邮箱
 * - 未登录（被设备拦截）：必须填写登录邮箱，匿名提交申请
 */
import { useState } from 'react';
import { Modal, Form, Input, Radio, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import { getDeviceId, getFingerprint, getDeviceName } from '@/lib/device';
import apiClient from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** 是否已登录（true=有 token，省略邮箱；false=登录页被拦场景，需填邮箱） */
  loggedIn?: boolean;
}

export default function DeviceApplyModal({ open, onClose, onSuccess, loggedIn = false }: Props) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload: Record<string, unknown> = {
        device_id: getDeviceId(),
        device_name: values.device_name || getDeviceName(),
        reason: values.reason || '',
        request_type: values.request_type || 'extra',
        fingerprint: getFingerprint(),
      };
      if (!loggedIn && values.email) {
        payload.email = values.email.trim().toLowerCase();
      }
      if (values.request_type === 'temp' && values.expires_at) {
        payload.expires_at = values.expires_at.toISOString();
      }
      const res = await apiClient.post('/auth/devices/apply', payload);
      if (res.data?.id) {
        message.success('绑定申请已提交，请等待系统管理员审批');
        form.resetFields();
        onSuccess?.();
        onClose();
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || '提交失败';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="设备绑定申请"
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="提交申请"
      cancelText="取消"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ request_type: 'extra' }}>
        {!loggedIn && (
          <Form.Item
            name="email"
            label="登录邮箱"
            rules={[
              { required: true, message: '请输入您的登录邮箱' },
              { type: 'email', message: '邮箱格式不正确' },
            ]}
            extra="用于管理员核实您的身份"
          >
            <Input placeholder="请输入系统登录邮箱" />
          </Form.Item>
        )}
        <Form.Item
          name="device_name"
          label="设备名称"
          rules={[{ required: true, message: '请输入设备名称（如：出差用笔记本）' }]}
        >
          <Input placeholder="例如：出差用笔记本 / 家中台式机" />
        </Form.Item>
        <Form.Item name="request_type" label="授权类型">
          <Radio.Group>
            <Radio value="extra">永久加绑</Radio>
            <Radio value="temp">临时授权</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.request_type !== cur.request_type}
        >
          {({ getFieldValue }) =>
            getFieldValue('request_type') === 'temp' ? (
              <Form.Item
                name="expires_at"
                label="授权有效期"
                rules={[{ required: true, message: '请选择授权到期时间' }]}
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  disabledDate={(d) => d && d.isBefore(dayjs().startOf('day'))}
                  placeholder="选择到期时间"
                />
              </Form.Item>
            ) : null
          }
        </Form.Item>
        <Form.Item name="reason" label="申请原因">
          <Input.TextArea
            rows={3}
            placeholder="请说明申请原因（如：出差需要、更换电脑等）"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
